import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { Compass, Target, Eye, Users, GraduationCap, FlaskConical, Network, Shield, ArrowUpRight } from "lucide-react";

const MISSION = [
  { icon: Compass, title: "Why Cosmos Defence Was Created",
    desc: "Indian enterprises and citizens face a security landscape that's evolving faster than tools, teams or training can keep up. Cosmos Defence was created to close that gap — by integrating enterprise security, awareness and workforce development under one ecosystem." },
  { icon: Network, title: "Ecosystem Vision",
    desc: "Security is no longer a product. It's an ecosystem of technologies, people, partners and awareness programs operating in sync. We build, run and sustain that ecosystem for our customers." },
  { icon: Users, title: "Awareness Mission",
    desc: "Most cyber incidents in India start with a person, not a packet. Our awareness initiative is a long-term movement — across seniors, students, merchants and corporates — to make digital safety a household reflex." },
  { icon: GraduationCap, title: "Workforce Development Vision",
    desc: "We don't just deploy people — we develop them. Cosmos Defence is building a cyber talent pipeline aligned to Skill India that's deployable across SOC, engineering, awareness and program coordination roles." },
  { icon: FlaskConical, title: "Future Labs Vision",
    desc: "The next decade of defence will be AI-assisted. Cosmos Defence Labs invests in detection, automation, deepfake and threat-intelligence research — so the SOC we operate in five years is already taking shape today." },
  { icon: Shield, title: "Alliance Ecosystem",
    desc: "We don't reinvent — we integrate. Our alliance ecosystem (Bitdefender, Safetica, Axidian, Zaperon, VMRay, StormWall, GoldPhish and more) is engineered into outcomes for Indian and global enterprises." },
  { icon: Target, title: "Long-Term Security Mission",
    desc: "Protecting people, enterprises and digital infrastructure — at scale, sustainably, and with the operational discipline that critical sectors deserve." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Cosmos Defence"
        title="Cybersecurity, awareness & digital safety — operating as one ecosystem"
        description="Cosmos Defence is an enterprise cybersecurity and digital safety ecosystem initiative under Cosmos Next Gen Tech Pvt Ltd (CNGTPL). We protect organisations, develop cyber talent, and drive citizen-facing digital safety across India."
      />

      <section className="py-20 bg-white">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="card-cd">
            <Target size={28} className="text-[#3B82F6]" />
            <h2 className="mt-4 font-display text-2xl font-bold text-[#0A1B5E]">Our Mission</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Protect people, enterprises and digital infrastructure — through modern cybersecurity, sustained awareness, and a workforce ecosystem built for the long term.
            </p>
          </div>
          <div className="card-cd ring-glow-purple border-purple-200">
            <Eye size={28} className="text-[#6D28D9]" />
            <h2 className="mt-4 font-display text-2xl font-bold text-[#0A1B5E]">Our Vision</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              A scalable, AI-assisted cybersecurity ecosystem where security is invisible, intelligent and inclusive — across enterprises, institutions and digital citizens.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F8FAFC]">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Mission Architecture"
            title="The seven principles that shape Cosmos Defence"
            description="Not a list of services — the operating beliefs behind every decision, every program and every deployment."
          />
          <div className="mt-14 space-y-5">
            {MISSION.map((m, i) => (
              <div key={m.title} data-testid={`mission-block-${i}`} className="card-cd !p-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-1">
                    <span className="h-12 w-12 grid place-items-center rounded-xl gradient-cta text-white">
                      <m.icon size={22} strokeWidth={1.6} />
                    </span>
                  </div>
                  <div className="lg:col-span-3">
                    <h3 className="font-display text-xl font-bold text-[#0A1B5E]">{m.title}</h3>
                  </div>
                  <div className="lg:col-span-8 text-slate-600 leading-relaxed">{m.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container-cd grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/ecosystem" className="card-cd !p-8 group" data-testid="about-link-ecosystem">
            <Network size={22} className="text-[#3B82F6]" />
            <h3 className="mt-4 font-display text-lg font-bold text-[#0A1B5E]">The Ecosystem</h3>
            <p className="mt-2 text-slate-600 text-sm">Six divisions operating as one integrated platform.</p>
            <span className="mt-4 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">Explore <ArrowUpRight size={14} /></span>
          </Link>
          <Link to="/alliances" className="card-cd !p-8 group" data-testid="about-link-alliances">
            <Shield size={22} className="text-[#3B82F6]" />
            <h3 className="mt-4 font-display text-lg font-bold text-[#0A1B5E]">Alliance Ecosystem</h3>
            <p className="mt-2 text-slate-600 text-sm">Best-in-class vendors engineered into customer outcomes.</p>
            <span className="mt-4 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">View alliances <ArrowUpRight size={14} /></span>
          </Link>
          <Link to="/labs" className="card-cd !p-8 group" data-testid="about-link-labs">
            <FlaskConical size={22} className="text-[#3B82F6]" />
            <h3 className="mt-4 font-display text-lg font-bold text-[#0A1B5E]">Future SOC & Labs</h3>
            <p className="mt-2 text-slate-600 text-sm">The AI security research roadmap shaping tomorrow's SOC.</p>
            <span className="mt-4 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">Enter labs <ArrowUpRight size={14} /></span>
          </Link>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
